import React from "react";
import { matrixTransformation } from "../util/matrixTransformation";
import { bradleyTerry } from "../util/bradleyTerry";
import { thurstoneMosteller } from "../util/thurstoneMosteller";
import { env } from "../../../config";

const HEADER_BG = "#374151";

const CARD     = { borderRadius: "8px", marginTop: "0", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" };
const CARD_HDR = { background: HEADER_BG, padding: "8px 14px", display: "flex", alignItems: "center", gap: "8px", borderRadius: "8px 8px 0 0" };
const SECTION  = { borderLeft: "1px solid #cbd5e1", borderRight: "1px solid #cbd5e1" };
const FOOTER   = { ...SECTION, borderBottom: "1px solid #cbd5e1", borderRadius: "0 0 8px 8px", padding: "5px 14px 6px", background: "#f9fafb", borderTop: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" };

const TH   = { background: HEADER_BG, color: "white", padding: "7px 10px", fontWeight: "600", fontSize: "11px", textAlign: "center", whiteSpace: "nowrap", cursor: "help", position: "sticky", top: 0, zIndex: 1 };
const CELL = { padding: "5px 10px", fontSize: "11px", textAlign: "center", verticalAlign: "middle", borderRight: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" };

function normalPhi(x) {
    const ax = Math.abs(x);
    const t  = 1 / (1 + 0.2316419 * ax);
    const pdf = Math.exp(-0.5 * ax * ax) / Math.sqrt(2 * Math.PI);
    const poly = ((((1.330274429 * t - 1.821255978) * t + 1.781477937) * t - 0.356563782) * t + 0.319381530) * t;
    const upper = pdf * poly;
    return x >= 0 ? 1 - upper : upper;
}

function lgamma(x) {
    const c = [76.18009172947146, -86.50532032941677, 24.01409824083091,
               -1.231739572450155, 0.001208650973866179, -5.395239384953e-6];
    let y = x, tmp = x + 5.5;
    tmp -= (x + 0.5) * Math.log(tmp);
    let ser = 1.000000000190015;
    for (let j = 0; j < 6; j++) ser += c[j] / ++y;
    return -tmp + Math.log(2.5066282746310005 * ser / x);
}

function gammaIncomplete(a, x) {
    if (x <= 0) return 0;
    if (x < a + 1) {
        let ap = a, del = 1 / a, sum = del;
        for (let n = 0; n < 300; n++) {
            ap++; del *= x / ap; sum += del;
            if (Math.abs(del) < Math.abs(sum) * 3e-12) break;
        }
        return sum * Math.exp(-x + a * Math.log(x) - lgamma(a));
    } else {
        let b = x + 1 - a, c = 1e30, d = 1 / b, h = d;
        for (let i = 1; i <= 300; i++) {
            const an = -i * (i - a);
            b += 2;
            d = an * d + b; if (Math.abs(d) < 1e-30) d = 1e-30;
            c = b + an / c; if (Math.abs(c) < 1e-30) c = 1e-30;
            d = 1 / d; const del = d * c; h *= del;
            if (Math.abs(del - 1) < 3e-12) break;
        }
        return 1 - Math.exp(-x + a * Math.log(x) - lgamma(a)) * h;
    }
}

function chi2pval(G2, df) {
    if (G2 <= 0 || df <= 0) return 1;
    return 1 - gammaIncomplete(df / 2, G2 / 2);
}

function sigStars(p) {
    if (p < 0.001) return " ***";
    if (p < 0.01)  return " **";
    if (p < 0.05)  return " *";
    return "";
}

function pStyle(val) {
    if (val < 0.001) return { background: "#bbf7d0", color: "#14532d", fontWeight: "700" };
    if (val < 0.05)  return { background: "#ecfdf5", color: "#166534", fontWeight: "600" };
    return { color: "#4b5563" };
}

export default function ParticipantOutlierDetection({ data, modelType, jwtToken, handleUpdate }) {
    if (!data || !data.twoAfcData) return null;

    const hideParticipant = (token, id) => {
        fetch(env.BACKEND_URL + `/participants/${id}`, {
            method: "DELETE",
            headers: { "Authorization": "Bearer " + token }
        }).then(() => handleUpdate());
    };

    const unhideParticipant = (token, id) => {
        fetch(env.BACKEND_URL + `/participants/${id}`, {
            method: "POST",
            headers: { "Authorization": "Bearer " + token }
        }).then(() => handleUpdate());
    };

    const transformed   = matrixTransformation(data);
    const methods       = transformed.sortedMethods;
    const methodCount   = methods.length;

    // hidden participants
    const rawHidden  = data.twoAfcData.hiddenParticipants;
    const hiddenPids = rawHidden instanceof Set
        ? [...rawHidden]
        : Array.isArray(rawHidden)
            ? rawHidden
            : (rawHidden ? Object.keys(rawHidden) : []);
    const hiddenSet = new Set(hiddenPids.map(String));
    const allChoices = data.twoAfcData.allParticipantChoices || data.twoAfcData.participantChoices || [];

    const fullMatrix = Array.from({ length: methodCount }, () => Array(methodCount).fill(0));
    allChoices.forEach(c => {
        const i = methods.indexOf(c.valueA);
        const j = methods.indexOf(c.valueB);
        if (i < 0 || j < 0) return;
        if (c.chosen === c.valueA) fullMatrix[i][j]++;
        else                       fullMatrix[j][i]++;
    });

    const fullTransformed = { ...transformed, matrix: fullMatrix, reorderedMatrix: fullMatrix };
    const globalModel = modelType === "BT"
        ? bradleyTerry(fullTransformed)
        : thurstoneMosteller(fullTransformed);
    const globalBetas = globalModel.betas;

    function globalProb(i, j) {
        if (modelType === "BT") {
            return 1 / (1 + Math.exp(globalBetas[j] - globalBetas[i]));
        } else {
            return normalPhi((globalBetas[i] - globalBetas[j]) / Math.sqrt(2));
        }
    }

    // per-participant win matrices
    const allParticipants     = [...new Set(allChoices.map(c => c.participantId))];
    const participantMatrices = {};

    allParticipants.forEach(pid => {
        const pm = Array.from({ length: methodCount }, () => Array(methodCount).fill(0));
        allChoices
            .filter(c => c.participantId === pid)
            .forEach(c => {
                const i = methods.indexOf(c.valueA);
                const j = methods.indexOf(c.valueB);
                if (i < 0 || j < 0) return;
                if (c.chosen === c.valueA) pm[i][j]++;
                else                       pm[j][i]++;
            });
        participantMatrices[pid] = pm;
    });

    const participants = allParticipants.filter(pid => !hiddenSet.has(String(pid)));

    const outlierStats = {};
    allParticipants.forEach(pid => {
        const pm = participantMatrices[pid];
        let G2 = 0, dfp = 0;
        for (let i = 0; i < methodCount; i++) {
            for (let j = i + 1; j < methodCount; j++) {
                const n = pm[i][j] + pm[j][i];
                if (n === 0) continue;
                dfp++;
                const Pij = globalProb(i, j);
                const Eij = n * Pij;
                const Eji = n * (1 - Pij);
                if (pm[i][j] > 0 && Eij > 1e-10) G2 += pm[i][j] * Math.log(pm[i][j] / Eij);
                if (pm[j][i] > 0 && Eji > 1e-10) G2 += pm[j][i] * Math.log(pm[j][i] / Eji);
            }
        }
        G2 *= 2;
        const pval        = chi2pval(G2, dfp);
        const isOutlier   = pval < 0.01;
        const isPotential = pval >= 0.01 && pval < 0.05;
        outlierStats[pid] = { G2, df: dfp, pval, isOutlier, isPotential };
    });

    const statsSortedPids = [...participants].sort((a, b) => outlierStats[a].pval - outlierStats[b].pval);
    const nOutliers  = participants.filter(pid => outlierStats[pid].isOutlier).length;
    const nPotential = participants.filter(pid => outlierStats[pid].isPotential).length;

    const modelTag = modelType === "BT" ? "Bradley–Terry" : "Thurstone–Mosteller";

    return (
        <div style={{ ...CARD, width: "fit-content" }}>

            <div style={CARD_HDR}>
                <h4 style={{ margin: 0, color: "white", fontSize: "13px", fontWeight: "600" }}>Pérez-Ortiz &amp; Mantiuk (2017)</h4>
                <span style={{ color: "rgba(255,255,255,0.8)", fontSize: "10px", padding: "1px 6px", borderRadius: "4px" }}>
                    {modelTag}
                </span>
                <span style={{ marginLeft: "auto", display: "flex", gap: "6px", alignItems: "center" }}>
                    {nOutliers > 0 && (
                        <span style={{ background: "#ef4444", color: "white", fontSize: "11px", fontWeight: "700", padding: "2px 8px", borderRadius: "10px" }}>
                            {nOutliers} outlier{nOutliers > 1 ? "s" : ""}
                        </span>
                    )}
                    {nPotential > 0 && (
                        <span style={{ background: "#f97316", color: "white", fontSize: "11px", fontWeight: "600", padding: "2px 8px", borderRadius: "10px" }}>
                            {nPotential} potential
                        </span>
                    )}
                    {nOutliers === 0 && nPotential === 0 && (
                        <span style={{ background: "#16a34a", color: "white", fontSize: "11px", fontWeight: "600", padding: "2px 8px", borderRadius: "10px" }}>
                            No outliers
                        </span>
                    )}
                </span>
            </div>

            <div style={{ ...SECTION, padding: "4px 14px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280" }}>
                G&sup2; likelihood-ratio goodness-of-fit test per participant.
            </div>

            {/* table */}
            <div style={{ ...SECTION, background: "white", overflowX: "auto", overflowY: "auto", maxHeight: "725px" }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                    <thead>
                        <tr>
                            <th title="Hide or unhide this participant" style={{ ...TH, borderRight: "1px solid #4b5563" }}>Action</th>
                            <th title="Stable participant ID" style={{ ...TH, borderRight: "1px solid #4b5563" }}>Participant ID</th>
                            <th title="G² = 2·Σ n_ij·ln(n_ij / E_ij): likelihood-ratio statistic" style={{ ...TH, borderRight: "1px solid #4b5563" }}>G² statistic</th>
                            <th title="Degrees of freedom = unique method pairs this participant compared" style={{ ...TH, borderRight: "1px solid #4b5563" }}>df</th>
                            <th title="p-value from χ²(df). p < 0.01 → outlier; p < 0.05 → potential" style={{ ...TH, borderRight: "1px solid #4b5563" }}>p-value</th>
                            <th title="Outlier: p < 0.01 | Potential: p < 0.05 | Normal: p ≥ 0.05" style={{ ...TH, borderRight: "none" }}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {statsSortedPids.map((pid, rowIdx) => {
                            const st    = outlierStats[pid];
                            const rowBg = st.isOutlier ? "#fff5f5" : st.isPotential ? "#fff7ed" : (rowIdx % 2 === 0 ? "white" : "#f8fafc");
                            return (
                                <tr key={pid} style={{ background: rowBg }}>
                                    <td style={{ ...CELL }}>
                                        <button
                                            className={`btn btn-xs ${st.isOutlier ? "btn-danger" : st.isPotential ? "btn-warning" : "btn-default"}`}
                                            style={{ fontSize: "11px", padding: "2px 8px", fontWeight: "600" }}
                                            onClick={() => hideParticipant(jwtToken, pid)}
                                        >
                                            Hide
                                        </button>
                                    </td>
                                    <td style={{ ...CELL, fontWeight: "700", fontFamily: "monospace", color: st.isOutlier ? "#ef4444" : st.isPotential ? "#f97316" : "#374151" }}>
                                        {pid}
                                    </td>
                                    <td style={{ ...CELL, fontFamily: "monospace" }}>{st.G2.toFixed(3)}</td>
                                    <td style={{ ...CELL, color: "#6b7280" }}>{st.df}</td>
                                    <td
                                        title={
                                            st.pval < 0.001 ? "Highly significant: strong outlier ***" :
                                            st.pval < 0.01  ? "Significant: outlier **" :
                                            st.pval < 0.05  ? "Borderline: potential outlier *" :
                                            "Not significant: consistent with group model"
                                        }
                                        style={{ ...CELL, ...pStyle(st.pval), cursor: "help" }}
                                    >
                                        {st.pval < 0.0001 ? "< 0.0001" : st.pval.toFixed(4)}{sigStars(st.pval)}
                                    </td>
                                    <td style={{ ...CELL, fontWeight: "600", color: st.isOutlier ? "#ef4444" : st.isPotential ? "#f97316" : "#16a34a", borderRight: "none" }}>
                                        {st.isOutlier   ? "⚠ Outlier"  : null}
                                        {st.isPotential ? "△ Potential" : null}
                                        {!st.isOutlier && !st.isPotential ? "✓ Normal" : null}
                                    </td>
                                </tr>
                            );
                        })}

                    </tbody>
                </table>
            </div>

            {/* hidden participants */}
            {hiddenPids.length > 0 && (
                <>
                    <div style={{ ...SECTION, background: "#f1f5f9", borderTop: "2px dashed #94a3b8", borderBottom: "1px solid #e2e8f0", padding: "5px 12px", fontSize: "11px", color: "#64748b", fontStyle: "italic" }}>
                        🚫 Hidden participants: excluded from analysis. Click Unhide to restore.
                    </div>
                    <div style={{ ...SECTION, background: "white", overflowX: "auto", overflowY: "auto", maxHeight: "145px" }}>
                        <table style={{ borderCollapse: "collapse", width: "100%" }}>
                            <thead>
                                <tr>
                                    <th style={{ ...TH, borderRight: "1px solid #4b5563" }}>Action</th>
                                    <th style={{ ...TH, borderRight: "1px solid #4b5563" }}>Participant ID</th>
                                    <th style={{ ...TH, borderRight: "1px solid #4b5563" }}>G² statistic</th>
                                    <th style={{ ...TH, borderRight: "1px solid #4b5563" }}>df</th>
                                    <th style={{ ...TH, borderRight: "1px solid #4b5563" }}>p-value</th>
                                    <th style={{ ...TH, borderRight: "none" }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {hiddenPids.map(hpid => {
                                    const st = outlierStats[hpid];
                                    if (!st) return (
                                        <tr key={"h-" + hpid} style={{ background: "#f8fafc", opacity: 0.65 }}>
                                            <td style={{ ...CELL }}><button className="btn btn-xs btn-primary" style={{ fontSize: "11px", padding: "2px 8px", fontWeight: "600" }} onClick={() => unhideParticipant(jwtToken, hpid)}>Unhide</button></td>
                                            <td style={{ ...CELL, fontWeight: "700", fontFamily: "monospace", color: "#94a3b8" }}>{hpid}</td>
                                            <td colSpan={4} style={{ ...CELL, color: "#94a3b8", fontStyle: "italic", borderRight: "none" }}>No data</td>
                                        </tr>
                                    );
                                    return (
                                        <tr key={"h-" + hpid} style={{ background: "#f8fafc", opacity: 0.75 }}>
                                            <td style={{ ...CELL }}>
                                                <button className="btn btn-xs btn-primary" style={{ fontSize: "11px", padding: "2px 8px", fontWeight: "600" }} onClick={() => unhideParticipant(jwtToken, hpid)}>Unhide</button>
                                            </td>
                                            <td style={{ ...CELL, fontWeight: "700", fontFamily: "monospace", color: "#94a3b8" }}>{hpid}</td>
                                            <td style={{ ...CELL, fontFamily: "monospace", color: "#94a3b8" }}>{st.G2.toFixed(3)}</td>
                                            <td style={{ ...CELL, color: "#94a3b8" }}>{st.df}</td>
                                            <td style={{ ...CELL, color: "#94a3b8" }}>{st.pval < 0.0001 ? "< 0.0001" : st.pval.toFixed(4)}</td>
                                            <td style={{ ...CELL, color: "#94a3b8", fontStyle: "italic", borderRight: "none" }}>Hidden</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <div style={FOOTER}>
                <span>G² = goodness-of-fit (higher = worse fit)</span>
                <span>|</span>
                <span>df = unique pairs compared</span>
                <span>|</span>
                <span style={{ color: "#ef4444", fontWeight: "600" }}>⚠ Outlier</span><span>p &lt; 0.01</span>
                <span>|</span>
                <span style={{ color: "#f97316", fontWeight: "600" }}>△ Potential</span><span>p &lt; 0.05</span>
                <span>|</span>
                <span>*** &lt;0.001 &nbsp; ** &lt;0.01 &nbsp; * &lt;0.05</span>
            </div>
        </div>
    );
}
